import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Conditional from '../../common/conditional';
import Button from '../../themed/button';
import Form from '../../common/form';

import toastService from '../../../services/toastService';
import { COUPLANT_OPTIONS, EQUIPMENT_OPTIONS, MATERIAL_OPTIONS, SCAN_DIRECTIONS, SCAN_SIDES, TECHNIQUES } from '../../../utils/constants';
import { transformListToOptions } from '../../../utils/utilities';
import { SWUTObservationItem } from './swutObservationItem';
import { SWUTProbeItem } from './swutProbeItem';

interface SWUTItem {
    id: number;
    surfaceCondition: string;
    dateOfInspection: Date;
    equipment: string;
    serialNumber: string;
    stageOfTest: string;
    weldJointType: string;
    weldType: string;
    weldMethod: string;
    weldID: string;
    weldThickness: number;
    surfaceTemperature: number;
    calibrationDueDate: Date;
    technique: string;
    calibrationBlock: number;
    scanSide: string;
    couplant: string;
    scanDirection: string;
    material: string;
    beamPath: string;
    scanMethod: string;
    observations: SWUTObservationItem[],
    probes: SWUTProbeItem[];
}

class SWUTItemForm extends Form {
	state = {
		data: {
			surfaceCondition: '',
			dateOfInspection: new Date(),
            equipment: '',
            serialNumber: '',
            stageOfTest: '',
            weldJointType: '',
            weldType: '',
            weldMethod: '',
            weldID: '',
            weldThickness: 0,
            surfaceTemperature: 0,
			calibrationDueDate: new Date(),

            technique: '',
            calibrationBlock: 0,
            scanSide: '',
            couplant: '',
            scanDirection: '',
            material: '',
            beamPath: '',
            scanMethod: '',
		},
		errors: {},
		loading: false,
		touched: false,
		progress: 0,
	};

	schema = {
        id: Joi.number().optional().label('ID'),
        surfaceCondition: Joi.string().required().label('Surface Condition'),
        equipment: Joi.string().required().label('Equipment'),
        serialNumber: Joi.string().required().label('Equipment Serial Number'),
        stageOfTest: Joi.string().required().label('Stage of Test'),
        weldJointType: Joi.string().required().label('Weld Joint Type'),
        weldType: Joi.string().required().label('Weld Type'),
        weldMethod: Joi.string().required().label('Weld Method'),
        weldID: Joi.string().required().label('Welder ID'),
        weldThickness: Joi.number().min(3).max(200).required().label('Weld Thickness'),
        surfaceTemperature: Joi.number().min(0).max(100).required().label('Surface Temperature'),
        dateOfInspection: Joi.date().less('now').required().label('Date of Inspection'),
        calibrationDueDate: Joi.date().greater('now').required().label('Calibration Due Date'),
        technique: Joi.string().required().label('Technique'),
        calibrationBlock: Joi.number().positive().required().label('Calibration Block (mm)'),
        scanSide: Joi.string().required().label('Scan Side'),
        couplant: Joi.string().required().label('Couplant'),
        scanDirection: Joi.string().required().label('Scan Direction'),
        material: Joi.string().required().label('Material'),
        beamPath: Joi.string().required().label('Beam Path'),
        scanMethod: Joi.string().required().label('Scan Method'),
    };

    static propTypes = {
        initialSWUTItem: PropTypes.object,
        onDeleteItem: PropTypes.func,
        onSave: PropTypes.func,
    };

    componentDidMount(): void {
        if (this.props.initialSWUTItem) {
            this.setState({ data: this.props.initialSWUTItem });
        }
    }
	
    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ data: any; errors: any; progress: number; loading: boolean; touched?: boolean; }>, snapshot?: any): void {
        if (this.state.data.equipment && this.state.data.equipment !== prevState.data.equipment) {
            const option = EQUIPMENT_OPTIONS.find((option) => option.value === this.state.data.equipment);
            if (option) this.setState({ data: { ...this.state.data, serialNumber: option.serialNumber }});
        }
    }

	doSubmit = () => {
		this.props.onSave(this.state.data);

        if (!this.props.initialSWUTItem) {
            this.resetData();
        }

        toastService.success('SW Report Data Saved Successfully!');
	};

    resetData = () => {
        this.setState({
            data: {
                surfaceCondition: '',
                dateOfInspection: new Date(),
                equipment: '',
                serialNumber: '',
                stageOfTest: '',
                weldJointType: '',
                weldType: '',
                weldMethod: '',
                weldID: '',
                weldThickness: 0,
                surfaceTemperature: 0,
                calibrationDueDate: new Date(),
    
                technique: '',
                calibrationBlock: 0,
                scanSide: '',
                couplant: '',
                scanDirection: '',
                material: '',
                beamPath: '',
                scanMethod: '',
            },
        })
    };

	render() {
		return (
            <form autoComplete="off" onSubmit={this.handleSubmit}>
                <div className="row">
                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(SURFACE_CONDITIONS),
                            name: 'surfaceCondition',
                            label: 'Surface Condition',
                            placeholder: 'Select Surface Condition',
                        })}
                    </div>

                    <div className="col">
						{this.renderDatePicker({
							name: 'dateOfInspection',
							label: 'Date of Inspection',
							placeholderText: 'Select Date of Inspection',
							maxDate: new Date(),
						})}
					</div>
                    
                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(_.range(0, 101)),
                            name: 'surfaceTemperature',
                            label: 'Surface Temperature',
                            placeholder: 'Select Surface Temperature',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: EQUIPMENT_OPTIONS.map((option) => ({ id: option.value, name: option.label })),
                            name: 'equipment',
                            label: 'Equipment',
                            placeholder: 'Select Equipment',
                        })}
                    </div>

                    <div className="col">
                        {this.renderInput({
                            type: 'text',
                            name: 'serialNumber',
                            label: 'Equipment Serial Number',
                            placeholder: 'Select Equipment Serial Number',
                            disabled: true,
                        })}
                    </div>
                </div>

                <div className="my-4 divider"></div>
                
                <div className="row">
                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(STAGES_OF_TEST),
                            name: 'stageOfTest',
                            label: 'Stage of Test',
                            placeholder: 'Select Stage of Test',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(JOINT_TYPES),
                            name: 'weldJointType',
                            label: 'Weld Joint Type',
                            placeholder: 'Select Weld Joint Type',
                        })}
                    </div>

                    <div className="col">
						{this.renderDatePicker({
							name: 'calibrationDueDate',
							label: 'Calibration Due Date',
							placeholderText: 'Select Calibration Due Date',
							minDate: new Date(),
						})}
					</div>

                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(WELD_TYPES),
                            name: 'weldType',
                            label: 'Weld Type',
                            placeholder: 'Select Weld Type',
                        })}
                    </div>
                </div>
                
                <div className="my-4 divider"></div>
                
                <div className="row">
                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(_.range(3, 201)),
                            name: 'weldThickness',
                            label: 'Weld Thickness (mm)',
                            placeholder: 'Select Weld Thickness (mm)',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(WELD_METHODS),
                            name: 'weldMethod',
                            label: 'Weld Method',
                            placeholder: 'Select Weld Method',
                        })}
                    </div>

                    <div className="col">
                        {this.renderInput({
                            type: 'text',
                            name: 'weldID',
                            label: 'Welder ID',
                            placeholder: 'Enter Welder ID',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(TECHNIQUES),
                            name: 'technique',
                            label: 'Technique',
                            placeholder: 'Select Technique',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(_.range(3, 201)),
                            name: 'calibrationBlock',
                            label: 'Calibration Block                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                 (mm)',
                            placeholder: 'Select Calibration Block                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                 (mm)',
                        })}
                    </div>
                </div>
                
                <div className="my-4 divider"></div>
                
                <div className="row">
                    <div className="col">
                        {this.renderSelect({
                            options: SCAN_SIDES,
                            name: 'scanSide',
                            label: 'Scan Side',
                            placeholder: 'Select Scan Side',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: COUPLANT_OPTIONS,
                            name: 'couplant',
                            label: 'Couplant',
                            placeholder: 'Select Couplant',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: SCAN_DIRECTIONS,
                            name: 'scanDirection',
                            label: 'Scan Direction',
                            placeholder: 'Select Scan Direction',
                        })}
                    </div>
                    
                    <div className="col">
                        {this.renderSelect({
                            options: MATERIAL_OPTIONS,
                            name: 'material',
                            label: 'Material',
                            placeholder: 'Select Material',
                        })}
                    </div>
                </div>
                
                <div className="my-4 divider"></div>
                
                <div className="row">
                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(BEAM_PATHS),
                            name: 'beamPath',
                            label: 'Beam Path',
                            placeholder: 'Select Beam Path',
                        })}
                    </div>

                    <div className="col">
                        {this.renderSelect({
                            options: transformListToOptions(SCAN_METHODS),
                            name: 'scanMethod',
                            label: 'Scan Method',
                            placeholder: 'Select Scan Method',
                        })}
                    </div>
                </div>

                <div className="my-4 divider"></div>

                <div className="d-flex justify-content-end">
                    <Conditional isVisible={!!this.props.onDeleteItem}>
                        <div className="me-3">
                            <Button
                                onClick={this.props.onDeleteItem} 
                                icon="trash"
                                label="Delete" 
                                type="button"
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>

				    {this.renderButton('Save')}
                </div>
            </form>
		);
	}
}

const SURFACE_CONDITIONS = ['As Welded', 'Coated', 'Cleaned'];
const STAGES_OF_TEST = ['Pre-Weld', 'Post-Weld', 'After Heat Treatment', 'Root Weld'];
const JOINT_TYPES = ['Single V', 'Double V', 'Fillet Weld', 'T-Weld', 'K- Weld', 'Y-Weld'];
const WELD_TYPES = ['Butt Weld', 'Fillet Weld', 'Groove Weld', 'Lap Weld'];
const WELD_METHODS = ['TIG', 'MIG', 'SAW', 'SMAW'];
const BEAM_PATHS = ['Half Skip', 'Full Skip', '1/12 Skip'];
const SCAN_METHODS = ['DAC', 'TGS', 'BW Reference'];

export type { SWUTItem };
export default SWUTItemForm;
